import { useState, useEffect } from 'react';
import { Check, CreditCard, Tag, Crown, Clock, Info, ChevronDown, ChevronUp, Ticket, Shield, ArrowRight, Loader2, X, AlertTriangle } from 'lucide-react';
import { paymentApi, chatApi, offerApi, couponApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function Recharge() {
  const { user } = useAuthStore();
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [quota, setQuota] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [topupConfig, setTopupConfig] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isTopupProcessing, setIsTopupProcessing] = useState(false);
  const [studentOffers, setStudentOffers] = useState([]);
  const [isOfferProcessing, setIsOfferProcessing] = useState(null);
  const [billingType, setBillingType] = useState('monthly');
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [feeConfig, setFeeConfig] = useState(null);
  const [showRegGate, setShowRegGate] = useState(false);
  const [regFeeData, setRegFeeData] = useState(null);
  const [regCouponCode, setRegCouponCode] = useState('');
  const [regCouponDiscount, setRegCouponDiscount] = useState(null);
  const [regCouponLoading, setRegCouponLoading] = useState(false);
  const [regPayProcessing, setRegPayProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [pendingPaymentAction, setPendingPaymentAction] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [packagesRes, quotaRes, topupRes, offersRes] = await Promise.all([
        paymentApi.getPackages(),
        chatApi.getQuota(),
        paymentApi.getTopupConfig(),
        offerApi.getStudentOffers().catch(() => ({ data: [] }))
      ]);
      const pkgData = packagesRes.data;
      const rawPkgs = pkgData.packages || pkgData;
      const parsedPkgs = rawPkgs.map(p => ({ ...p, amount: parseFloat(p.amount) || 0 }));
      setPackages(parsedPkgs);
      setFeeConfig(pkgData.feeConfig || null);
      setQuota(quotaRes.data);
      setTopupConfig(topupRes.data);
      setStudentOffers(offersRes.data);
      const allPkgs = parsedPkgs;
      const monthlyPkgs = allPkgs.filter(p => !p.billing_type || p.billing_type === 'monthly');
      if (monthlyPkgs.length > 0) {
        setSelectedPackage(monthlyPkgs[1]?.id || monthlyPkgs[0].id);
      }
    } catch (error) {
      console.error('Failed to load packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPackages = packages.filter(p => {
    if (billingType === 'annual') return p.billing_type === 'annual';
    return !p.billing_type || p.billing_type === 'monthly';
  });

  const getMonthlyEquivalent = (annualPkg) => {
    // Find corresponding monthly package by name (strip " Annual")
    const monthlyName = annualPkg.name.replace(' Annual', '');
    return packages.find(p => p.name === monthlyName && (!p.billing_type || p.billing_type === 'monthly'));
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    const selectedPkg = packages.find(p => p.id === selectedPackage);
    if (!selectedPkg) {
      toast.error('Select a package first');
      return;
    }
    setCouponLoading(true);
    try {
      const res = await couponApi.validate(couponCode.trim(), selectedPkg.amount);
      setCouponDiscount(res.data);
      toast.success(`Coupon applied! You save ₹${res.data.discount}`);
    } catch (error) {
      setCouponDiscount(null);
      toast.error(error.response?.data?.error || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponDiscount(null);
  };

  const handleRecharge = async () => {
    if (!selectedPackage) return;

    setIsProcessing(true);
    try {
      const orderRes = await paymentApi.createRechargeOrder(selectedPackage, couponDiscount ? couponCode.trim() : null);
      const paymentUrl = orderRes.data?.paymentUrl;
      if (!paymentUrl) {
        toast.error('Payment provider did not return a URL. Please try again or contact support.');
        setIsProcessing(false);
        return;
      }
      window.location.href = paymentUrl;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to initiate payment');
      setIsProcessing(false);
    }
  };

  const handleOfferPurchase = async (offer) => {
    setIsOfferProcessing(offer.id);
    try {
      const orderRes = await offerApi.createOrder(offer.id);
      const { paymentUrl } = orderRes.data;

      window.location.href = paymentUrl;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to initiate payment');
      setIsOfferProcessing(null);
    }
  };

  const handleTopup = async () => {
    const amount = parseFloat(customAmount);
    if (!amount || amount < topupConfig.minAmount || amount > topupConfig.maxAmount) {
      toast.error(`Amount must be between ₹${topupConfig.minAmount} and ₹${topupConfig.maxAmount}`);
      return;
    }

    setIsTopupProcessing(true);
    try {
      const orderRes = await paymentApi.createTopupOrder(amount, couponDiscount ? couponCode.trim() : null);
      const paymentUrl = orderRes.data?.paymentUrl;
      if (!paymentUrl) {
        toast.error('Payment provider did not return a URL. Please try again or contact support.');
        setIsTopupProcessing(false);
        return;
      }
      window.location.href = paymentUrl;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to initiate payment');
      setIsTopupProcessing(false);
    }
  };

  // Registration gate: check if user needs to pay registration fee first
  const needsRegistration = user?.role === 'student' && user?.registrationPaid === false;

  const openRegGate = async () => {
    setShowRegGate(true);
    if (!regFeeData) {
      try {
        const res = await paymentApi.getRegistrationFee();
        setRegFeeData(res.data);
      } catch (error) {
        toast.error('Failed to load registration fee');
      }
    }
  };

  const handleRegApplyCoupon = async () => {
    if (!regCouponCode.trim() || !regFeeData) return;
    setRegCouponLoading(true);
    try {
      const res = await couponApi.validate(regCouponCode.trim(), regFeeData.total);
      setRegCouponDiscount(res.data);
      toast.success(`Coupon applied! You save ₹${res.data.discount}`);
    } catch (error) {
      setRegCouponDiscount(null);
      toast.error(error.response?.data?.error || 'Invalid coupon code');
    } finally {
      setRegCouponLoading(false);
    }
  };

  const handleRegPayment = async () => {
    setRegPayProcessing(true);
    try {
      const orderRes = await paymentApi.createRegistrationOrder(
        regCouponDiscount ? regCouponCode.trim() : null
      );
      window.location.href = orderRes.data.paymentUrl;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to initiate payment');
      setRegPayProcessing(false);
    }
  };

  const openPaymentConfirmation = (action) => {
    setPendingPaymentAction(() => action);
    // Pre-check the agreement so the "Proceed to Pay" button is enabled by default.
    // Users were missing the checkbox and seeing a disabled button as a "frozen" screen.
    setAgreedToTerms(true);
    setShowPaymentModal(true);
  };

  const confirmAndPay = () => {
    setShowPaymentModal(false);
    if (pendingPaymentAction) pendingPaymentAction();
    setPendingPaymentAction(null);
  };

  // Intercept recharge/topup if registration not paid
  const guardedRecharge = () => {
    if (needsRegistration) { openRegGate(); return; }
    openPaymentConfirmation(() => handleRecharge());
  };

  const guardedTopup = () => {
    if (needsRegistration) { openRegGate(); return; }
    openPaymentConfirmation(() => handleTopup());
  };

  const guardedOfferPurchase = (offer) => {
    if (needsRegistration) { openRegGate(); return; }
    openPaymentConfirmation(() => handleOfferPurchase(offer));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Recharge</h1>
      <p className="text-gray-600 mb-8">
        Purchase question credits to continue learning with Memory Coach
      </p>

      {/* Current Balance */}
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-primary-600 text-sm">Current Balance</p>
            <p className="text-3xl font-bold text-primary-900">
              {quota?.availableQuestions || 0} Questions
            </p>
            {quota?.nextResetDate && (
              <p className="text-primary-600 text-xs mt-1">
                Resets on: {new Date(quota.nextResetDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            )}
          </div>
          <CreditCard className="w-12 h-12 text-primary-400" />
        </div>
      </div>

      {/* Special Offers */}
      {studentOffers.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Tag className="w-5 h-5 mr-2 text-primary-600" />
            Special Offers for You
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {studentOffers.map((offer) => (
              <div
                key={offer.id}
                className="border-2 border-primary-300 bg-primary-50 rounded-xl p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900">{offer.name}</h3>
                {offer.description && (
                  <p className="text-sm text-gray-600 mt-1">{offer.description}</p>
                )}
                <div className="mt-3 flex items-baseline space-x-2">
                  <span className="text-3xl font-bold text-gray-900">₹{offer.amount}</span>
                  <span className="text-gray-600">{offer.questions} questions</span>
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  ₹{(offer.amount / offer.questions).toFixed(2)} per question
                </p>
                {offer.expires_at && (
                  <p className="text-xs text-amber-600 mt-2">
                    Expires: {new Date(offer.expires_at).toLocaleDateString()}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">From: {offer.created_by_name}</p>
                <button
                  onClick={() => guardedOfferPurchase(offer)}
                  disabled={isOfferProcessing === offer.id}
                  className="mt-4 w-full py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {isOfferProcessing === offer.id ? 'Processing...' : `Buy Now - ₹${offer.amount}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing Type Toggle */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Select Package</h2>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => { setBillingType('monthly'); setSelectedPackage(null); removeCoupon(); }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              billingType === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => { setBillingType('annual'); setSelectedPackage(null); removeCoupon(); }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center ${
              billingType === 'annual' ? 'bg-amber-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Crown className="w-4 h-4 mr-1" />
            Annual (Save 1 month!)
          </button>
        </div>
      </div>

      {/* Annual Banner */}
      {billingType === 'annual' && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-4 flex items-center">
          <Crown className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0" />
          <div>
            <p className="text-amber-900 font-semibold">Pay for 11 months, get 12!</p>
            <p className="text-amber-700 text-sm">Save one full month with annual billing</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {filteredPackages.map((pkg) => {
          const monthlyEquiv = billingType === 'annual' ? getMonthlyEquivalent(pkg) : null;
          const originalAnnualPrice = monthlyEquiv ? monthlyEquiv.amount * 12 : null;
          return (
            <div
              key={pkg.id}
              onClick={() => { setSelectedPackage(pkg.id); removeCoupon(); }}
              className={`
                relative border-2 rounded-xl p-6 cursor-pointer transition-all
                ${selectedPackage === pkg.id
                  ? billingType === 'annual' ? 'border-amber-500 bg-amber-50' : 'border-primary-500 bg-primary-50'
                  : billingType === 'annual' ? 'border-amber-200 hover:border-amber-300' : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              {selectedPackage === pkg.id && (
                <div className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center ${
                  billingType === 'annual' ? 'bg-amber-500' : 'bg-primary-500'
                }`}>
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900">
                {billingType === 'annual' ? pkg.name.replace(' Annual', '') : pkg.name}
              </h3>
              <div className="mt-2">
                {originalAnnualPrice && (
                  <p className="text-lg text-gray-400 line-through">₹{originalAnnualPrice}</p>
                )}
                <p className="text-3xl font-bold text-gray-900">₹{pkg.amount}</p>
              </div>
              <p className="text-gray-600 mt-1">
                {pkg.name === 'Standard' ? (
                  <>400 <span className="text-green-600 font-medium">+ 50 extra</span> questions</>
                ) : pkg.name === 'Premium' ? (
                  <>600 <span className="text-green-600 font-medium">+ 100 extra</span> questions</>
                ) : (
                  <>{pkg.questions} questions</>
                )}
              </p>
              <div className="flex items-center mt-2 space-x-2">
                <span className="inline-flex items-center text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  <Clock className="w-3 h-3 mr-1" />
                  Validity: {billingType === 'annual' ? '12 months' : '1 month'}
                </span>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                ₹{(pkg.amount / pkg.questions).toFixed(2)} per question
              </p>
            </div>
          );
        })}
      </div>

      {/* Coupon Code Section */}
      <div className="mb-4">
        <button
          onClick={() => setShowCoupon(!showCoupon)}
          className="flex items-center text-sm text-primary-600 hover:text-primary-700"
        >
          <Ticket className="w-4 h-4 mr-1" />
          {showCoupon ? 'Hide coupon code' : 'Have a coupon code?'}
          {showCoupon ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
        </button>
        {showCoupon && (
          <div className="mt-3 bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            {couponDiscount ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-700 dark:text-green-400 font-medium">
                    Coupon "{couponCode}" applied!
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    You save ₹{couponDiscount.discount} on this purchase
                  </p>
                </div>
                <button
                  onClick={removeCoupon}
                  className="text-sm text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 dark:hover:bg-primary-500 disabled:opacity-50 transition-colors"
                >
                  {couponLoading ? 'Checking...' : 'Apply'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Price Breakdown */}
      {selectedPackage && (() => {
        const pkg = packages.find(p => p.id === selectedPackage);
        if (!pkg) return null;
        const planFee = couponDiscount ? couponDiscount.finalAmount : pkg.amount;
        const gstPct = feeConfig?.gstPercent ?? 18;
        const svcPct = feeConfig?.serviceFeePercent ?? 2;
        const platformCharges = feeConfig?.platformCharges ?? 5;
        const gst = Math.round(planFee * gstPct) / 100;
        const serviceFee = Math.round(planFee * svcPct) / 100;
        const total = Math.round((planFee + gst + serviceFee + platformCharges) * 100) / 100;

        return (
          <div className="mb-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3 text-sm">Price Breakdown</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Plan fee</span>
                <span className="text-gray-800 dark:text-gray-100">₹{pkg.amount.toFixed(2)}</span>
              </div>
              {couponDiscount && (
                <div className="flex justify-between text-green-600 dark:text-green-400">
                  <span>Coupon discount</span>
                  <span>-₹{couponDiscount.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">GST ({gstPct}%)</span>
                <span className="text-gray-800 dark:text-gray-100">₹{gst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Service fee ({svcPct}%)</span>
                <span className="text-gray-800 dark:text-gray-100">₹{serviceFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Platform charges</span>
                <span className="text-gray-800 dark:text-gray-100">₹{platformCharges.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold pt-2 border-t border-gray-300 dark:border-gray-600 text-base text-gray-900 dark:text-white">
                <span>Total</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Recharge Button */}
      <button
        onClick={guardedRecharge}
        disabled={!selectedPackage || isProcessing}
        className="w-full py-4 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isProcessing ? 'Processing...' : (() => {
          const pkg = packages.find(p => p.id === selectedPackage);
          if (!pkg) return 'Proceed to Payment';
          const planFee = couponDiscount ? couponDiscount.finalAmount : pkg.amount;
          const gstPct = feeConfig?.gstPercent ?? 18;
          const svcPct = feeConfig?.serviceFeePercent ?? 2;
          const platChg = feeConfig?.platformCharges ?? 5;
          const gst = Math.round(planFee * gstPct) / 100;
          const serviceFee = Math.round(planFee * svcPct) / 100;
          const total = Math.round((planFee + gst + serviceFee + platChg) * 100) / 100;
          return `Pay ₹${total.toFixed(2)}`;
        })()}
      </button>

      {/* Custom Top-up */}
      {topupConfig?.enabled && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-4">Custom Top-up</h2>
          {!quota?.nextResetDate ? (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-center">
              <Info className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-amber-800 font-medium">No active recharge cycle</p>
              <p className="text-amber-700 text-sm mt-1">
                Purchase a recharge package first to enable top-ups. Top-up questions expire along with your recharge cycle.
              </p>
            </div>
          ) : (
          <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
              Enter a custom amount for a smaller recharge (₹{topupConfig.minAmount} - ₹{topupConfig.maxAmount})
            </p>
            <span className="inline-flex items-center text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full mb-4">
              <Clock className="w-3 h-3 mr-1" />
              Expires on: {new Date(quota.nextResetDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            <div className="flex items-center space-x-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Amount (₹)</label>
                <input
                  type="number"
                  min={topupConfig.minAmount}
                  max={topupConfig.maxAmount}
                  step={1}
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder={`Min ₹${topupConfig.minAmount}`}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="text-center min-w-[100px]">
                <p className="text-sm text-gray-500 dark:text-gray-400">You'll get</p>
                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {customAmount ? Math.floor(parseFloat(customAmount) * topupConfig.rate) || 0 : 0}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">questions</p>
              </div>
            </div>
            {/* Topup Price Breakdown */}
            {customAmount && parseFloat(customAmount) > 0 && (() => {
              const amt = parseFloat(customAmount);
              const gstPct = feeConfig?.gstPercent ?? 18;
              const svcPct = feeConfig?.serviceFeePercent ?? 2;
              const platChg = 3;
              const gst = Math.round(amt * gstPct) / 100;
              const svcFee = Math.round(amt * svcPct) / 100;
              const total = Math.round((amt + gst + svcFee + platChg) * 100) / 100;
              return (
                <div className="mt-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-sm space-y-1 text-gray-900 dark:text-gray-100">
                  <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-400">Amount</span><span>₹{amt.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-400">GST ({gstPct}%)</span><span>₹{gst.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-400">Service fee ({svcPct}%)</span><span>₹{svcFee.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-400">Platform charges</span><span>₹{platChg.toFixed(2)}</span></div>
                  <div className="flex justify-between font-semibold pt-1 border-t border-gray-300 dark:border-gray-600"><span>Total</span><span>₹{total.toFixed(2)}</span></div>
                </div>
              );
            })()}
            <button
              onClick={guardedTopup}
              disabled={!customAmount || isTopupProcessing}
              className="mt-4 w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isTopupProcessing ? 'Processing...' : (() => {
                if (!customAmount) return 'Top Up';
                const amt = parseFloat(customAmount);
                if (!amt) return 'Top Up';
                const gstPct = feeConfig?.gstPercent ?? 18;
                const svcPct = feeConfig?.serviceFeePercent ?? 2;
                const platChg = 3;
                const gst = Math.round(amt * gstPct) / 100;
                const svcFee = Math.round(amt * svcPct) / 100;
                const total = Math.round((amt + gst + svcFee + platChg) * 100) / 100;
                return `Top Up ₹${total.toFixed(2)}`;
              })()}
            </button>
          </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="mt-8">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start">
          <Info className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-amber-800 font-medium text-sm">About AI Credit Validity</p>
            <p className="text-amber-700 text-sm mt-1">
              AI credits are valid for 1 month from your purchase date. Top-up credits expire along with your current recharge cycle.
            </p>
          </div>
        </div>
        <p className="text-center text-sm text-gray-500 mt-3">
          Secure payment powered by Instamojo
        </p>
      </div>

      {/* Payment Confirmation Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 60 }}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center mr-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Important</h3>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
              <ul className="text-sm text-amber-800 dark:text-amber-200 space-y-2">
                <li>1. Please <strong>do not close the browser</strong> or press back during the payment process.</li>
                <li>2. After successful payment, you will be <strong>automatically redirected</strong> back to the website.</li>
                <li>3. Wait for the redirect to complete — <strong>do not navigate away</strong> until you see the confirmation.</li>
                <li>4. If payment gets stuck, contact support or the admin can recover it from the dashboard.</li>
              </ul>
            </div>

            <label className="flex items-start space-x-3 mb-5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">
                I have read the above instructions and agree to the{' '}
                <a href="/privacy-policy" target="_blank" className="text-primary-600 dark:text-primary-400 underline">Privacy Policy</a>
              </span>
            </label>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndPay}
                disabled={!agreedToTerms}
                className="flex-1 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex items-center justify-center"
              >
                Proceed to Pay <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration Payment Gate Modal */}
      {showRegGate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Shield className="w-6 h-6 text-primary-600 dark:text-primary-400 mr-2" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Registration Required</h2>
              </div>
              <button onClick={() => setShowRegGate(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
              To purchase question credits, you need to complete your platform registration first.
            </p>

            {regFeeData ? (
              <>
                {/* Fee Breakdown */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                  <h3 className="font-semibold text-gray-800 mb-3 text-sm">Registration Fee</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Course Fee</span>
                      <span className="text-gray-800">₹{regFeeData.courseFee?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service Fee</span>
                      <span className="text-gray-800">₹{regFeeData.serviceFee}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Platform Fee</span>
                      <span className="text-gray-800">₹{regFeeData.platformFee}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">GST</span>
                      <span className="text-gray-800">₹{regFeeData.gst}</span>
                    </div>
                    <div className="flex justify-between pt-1 border-t border-gray-200 mt-1">
                      <span className="text-gray-600">Starter Pack ({regFeeData.freeQuestions} Questions)</span>
                      <span className="text-gray-800">₹{regFeeData.starterPackBase}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Starter Pack GST (18%)</span>
                      <span className="text-gray-800">₹{regFeeData.starterPackGst}</span>
                    </div>
                    {regCouponDiscount && (
                      <div className="flex justify-between text-green-600">
                        <span>Coupon Discount</span>
                        <span>-₹{regCouponDiscount.discount}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold pt-2 border-t border-gray-300 text-base">
                      <span>Total</span>
                      <span className="text-primary-700">₹{regCouponDiscount
                        ? regCouponDiscount.finalAmount.toLocaleString('en-IN')
                        : regFeeData.total.toLocaleString('en-IN')
                      }</span>
                    </div>
                  </div>
                </div>

                {/* Coupon */}
                <div className="mb-4">
                  {regCouponDiscount ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-green-700 font-medium text-sm">"{regCouponCode}" applied!</p>
                        <p className="text-xs text-green-600">You save ₹{regCouponDiscount.discount}</p>
                      </div>
                      <button
                        onClick={() => { setRegCouponCode(''); setRegCouponDiscount(null); }}
                        className="text-sm text-red-500 hover:text-red-600 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={regCouponCode}
                        onChange={(e) => setRegCouponCode(e.target.value.toUpperCase())}
                        placeholder="Coupon code"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        onClick={handleRegApplyCoupon}
                        disabled={regCouponLoading || !regCouponCode.trim()}
                        className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                      >
                        {regCouponLoading ? '...' : 'Apply'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Pay Button */}
                <button
                  onClick={() => openPaymentConfirmation(() => handleRegPayment())}
                  disabled={regPayProcessing}
                  className="w-full py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center"
                >
                  {regPayProcessing ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing...</>
                  ) : (
                    <>Pay ₹{regCouponDiscount
                      ? regCouponDiscount.finalAmount.toLocaleString('en-IN')
                      : regFeeData.total.toLocaleString('en-IN')
                    } <ArrowRight className="w-5 h-5 ml-2" /></>
                  )}
                </button>
                <p className="text-center text-xs text-gray-400 mt-2">
                  After registration, you can purchase question credits
                </p>
              </>
            ) : (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Recharge;
